import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography
} from '@mui/material';
import { MuiModal } from 'ui/MuiModal';

export interface CopyTargetItem {
    id: string | number;
    label: string;
    closed: boolean;
}

interface IProps {
    open: boolean;
    sourceLabel: string;
    items: CopyTargetItem[];
    onClose: () => void;
    onSubmit: (ids: (string | number)[]) => void;
}

type FormProps = Omit<IProps, 'open'>;

const CopyTimesConfigurationForm: React.FC<FormProps> = ({ sourceLabel, items, onClose, onSubmit }) => {
    const defaultChecked = React.useMemo(
        () => items.filter(item => !item.closed).map(item => item.id),
        [items]
    );
    const [checked, setChecked] = React.useState<(string | number)[]>(defaultChecked);

    const allSelected = items.every(item => checked.includes(item.id));

    const handleToggleAll = React.useCallback(() => {
        setChecked(allSelected ? [] : items.map(item => item.id));
    }, [allSelected, items]);

    const handleToggle = (id: string | number) => () => {
        const currentIndex = checked.indexOf(id);
        const newChecked = [...checked];
        if (currentIndex === -1) {
            newChecked.push(id);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setChecked(newChecked);
    };

    const handleSubmit = React.useCallback(() => {
        onSubmit(checked);
    }, [checked, onSubmit]);

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="column" gap={2} justifyContent="space-between">
                <Typography variant="body1">
                    Copy the opening hours configuration from <b>{sourceLabel}</b> to others
                </Typography>
                <Divider style={{ width: '100%' }} />
            </Box>
            <List sx={{ width: '100%', py: 0 }}>
                <ListItem disablePadding>
                    <ListItemButton role={undefined} onClick={handleToggleAll}>
                        <ListItemIcon>
                            <Checkbox edge="start" checked={allSelected} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary="Select All" />
                    </ListItemButton>
                </ListItem>
                {items.map(item => (
                    <ListItem key={item.id} disablePadding>
                        <ListItemButton role={undefined} onClick={handleToggle(item.id)}>
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={checked.includes(item.id)}
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                secondary={item.closed ? 'Closed' : 'Open'}
                                slotProps={{
                                    secondary: {
                                        color: item.closed ? 'text.secondary' : 'text.primary',
                                        variant: 'caption'
                                    }
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Box display="flex" flexDirection="row" gap={1} justifyContent="flex-end">
                <Button variant="outlined" color="primary" size="small" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" size="small" onClick={handleSubmit}>
                    Copy
                </Button>
            </Box>
        </Box>
    );
};

export const CopyTimesConfigurationModal: React.FC<IProps> = ({
    open,
    sourceLabel,
    items,
    onClose,
    onSubmit
}) => (
    <MuiModal open={open} onClose={onClose} sx={{ width: '400px' }}>
        <CopyTimesConfigurationForm
            sourceLabel={sourceLabel}
            items={items}
            onClose={onClose}
            onSubmit={onSubmit}
        />
    </MuiModal>
);
